import { useRouter, type Href } from 'expo-router';
import React from 'react';
import { Platform } from 'react-native';
import { twJoin } from 'tailwind-merge';

import type { AnonymousDirectoryCardProps } from './types';

import { TrackedLink as TrackedRouterLink } from '@/components/TrackedLink';
import { ResponseTime } from '@/components/responseTime';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { Button, ButtonGroup, ButtonText } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Divider } from '@/components/ui/divider';
import { HStack } from '@/components/ui/hstack';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { ANONYMOUS_EXPERT } from '@/constants/users';
import { segmentClient } from '@/util/analytics';
import { getNativeTextProps } from '@/util/getNativeTextProps';

const isWeb = Platform.OS === 'web';

const RESPONSE_TIME_LABEL = 'Response time varies';

type CardContentProps = {
  item: { id: string | number };
  isMultiColumn: boolean;
  buildInquiryUrl: (expertId: string | number) => Href;
};

/**
 * Internal card content component for AnonymousDirectoryCard.
 * Renders the anonymous expert option using ANONYMOUS_EXPERT constants.
 * Memoized to prevent unnecessary re-renders during list scrolling.
 */
const CardContent = React.memo(function CardContent({
  item,
  isMultiColumn,
  buildInquiryUrl,
}: CardContentProps) {
  const { navigate } = useRouter();

  return (
    <Card
      variant="outline"
      testID="anonymous-card"
      className={twJoin(isMultiColumn && 'hover:shadow-hard-1', 'p-0 py-4 web:h-full web:flex-1')}>
      <HStack className="items-center gap-6 px-4 web:gap-3">
        <Avatar size="md">
          <AvatarImage source={{ uri: ANONYMOUS_EXPERT.avatarSrc }} />
        </Avatar>
        <VStack className="flex-1 justify-center" space="xs">
          <Text
            size={isMultiColumn ? 'md' : 'lg'}
            className="flex-1 font-medium text-typography-900"
            {...getNativeTextProps()}
            isTruncated>
            {ANONYMOUS_EXPERT.name}
          </Text>
        </VStack>
      </HStack>
      <Divider className="my-4" />
      <VStack className="mb-4 px-4">
        <VStack className="gap-2">
          <Text className="text-sm text-typography-700">
            {ANONYMOUS_EXPERT.directoryCardDescription}
          </Text>
          <Text className="text-sm text-typography-700">
            {ANONYMOUS_EXPERT.directoryCardFocusAreas}
          </Text>
        </VStack>
      </VStack>
      <VStack className="mx-4">
        <ResponseTime
          responseTime={undefined}
          responseTimeLabel={RESPONSE_TIME_LABEL}
          fontSize="text-xs"
        />
      </VStack>
      <ButtonGroup className="mt-4 flex-row justify-center gap-4 px-4 md:hidden">
        <Button
          size="xs"
          onPress={(e) => {
            segmentClient.track(ANALYTICS_EVENTS.EXPERT_SELECTED, {
              expert_id: item.id,
            });

            navigate(`/profile/${item.id}`);

            e.preventDefault();
          }}
          variant="outline"
          className="flex-1 web:md:hidden">
          <ButtonText>View Profile</ButtonText>
        </Button>
        <Button
          size="xs"
          onPress={(e) => {
            segmentClient.track(ANALYTICS_EVENTS.DIRECTORY_MESSAGE_SELECTED, {
              expert_id: item.id,
              expert_name: ANONYMOUS_EXPERT.name,
            });

            navigate(buildInquiryUrl(item.id));

            e.preventDefault();
          }}
          className="flex-1 web:flex-auto">
          <ButtonText>Message</ButtonText>
        </Button>
      </ButtonGroup>
    </Card>
  );
});

/**
 * AnonymousDirectoryCard - Card for "Ask Anonymously" option
 *
 * Features:
 * - Uses ANONYMOUS_EXPERT constants for all content
 * - No speciality badge, institution, or title
 * - "Response time varies" label
 * - Always placed first in directory listing
 * - Profile and inquiry navigation
 */
export const AnonymousDirectoryCard = React.memo(function AnonymousDirectoryCard({
  item,
  isMultiColumn,
  buildInquiryUrl,
}: AnonymousDirectoryCardProps) {
  const content = (
    <CardContent item={item} isMultiColumn={isMultiColumn} buildInquiryUrl={buildInquiryUrl} />
  );

  if (isMultiColumn) {
    return (
      <TrackedRouterLink
        asChild
        href={buildInquiryUrl(item.id)}
        trackingEvent={ANALYTICS_EVENTS.DIRECTORY_MESSAGE_SELECTED}
        trackingData={{ expert_id: item.id, expert_name: ANONYMOUS_EXPERT.name }}>
        <Pressable className={twJoin('h-full', isWeb && 'flex-1 justify-between')}>
          {content}
        </Pressable>
      </TrackedRouterLink>
    );
  }

  return content;
});
